import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Users } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["vendor", "expert", "employee", "other"];
const emptyForm = { name: "", type: "vendor", account_number: "", note: "", active: true };

export const BeneficiariesAdmin = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("field_required")); return; }
    try {
      const payload = { ...form, account_number: form.account_number || null, note: form.note || null };
      if (editing) await api.put(`/beneficiaries/${editing.id}`, payload);
      else await api.post("/beneficiaries", payload);
      toast.success(t("beneficiary_saved"));
      setOpen(false);
      await cfg.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const list = cfg?.beneficiaries || [];

  return (
    <div className="space-y-4" data-testid="beneficiaries-admin">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{t("beneficiaries_desc")}</p>
        <Button data-testid="add-beneficiary-button" className="h-9 gap-2 rounded-full bg-orange-500 hover:bg-orange-600"
          onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}>
          <Plus className="h-4 w-4" /> {t("add_beneficiary")}
        </Button>
      </div>

      {list.length === 0 ? (
        <Card className="border-dashed p-12 text-center text-slate-400" data-testid="no-beneficiaries">
          <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm">{t("no_beneficiaries")}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">{t("name")}</th>
                <th className="px-4 py-3">{t("type")}</th>
                <th className="px-4 py-3">{t("account_number")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`beneficiary-row-${b.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{t(`ben_${b.type}`)}</td>
                  <td className="px-4 py-3 text-xs mono text-slate-500">{b.account_number || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={b.active ? "font-semibold text-emerald-600" : "text-slate-400"}>
                      {t(b.active ? "active" : "inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-beneficiary-${b.id}`} className="p-1.5 text-slate-400 hover:text-slate-900"
                      onClick={() => { setEditing(b); setForm({ ...b, account_number: b.account_number || "", note: b.note || "" }); setOpen(true); }}>
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_beneficiary")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("name")}*</Label>
              <Input data-testid="beneficiary-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("type")}</Label>
              <select data-testid="beneficiary-type-select" value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {TYPES.map((x) => <option key={x} value={x}>{t(`ben_${x}`)}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">{t("account_number")}</Label>
              <Input data-testid="beneficiary-account-input" className="mono" value={form.account_number}
                onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("note")}</Label>
              <Input data-testid="beneficiary-note-input" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="beneficiary-active-checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              {t("active")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-beneficiary-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BeneficiariesAdmin;
